import { useEffect, useMemo, useState } from 'react';
import { toIsoDate } from '../utils/dateUtils';
import BanquetHelpPanel from './BanquetHelpPanel';

const WINDOW_DAYS = 7;

const addDaysIso = (isoDate, days) => {
  const dt = new Date(`${isoDate}T00:00:00`);
  dt.setDate(dt.getDate() + days);
  return toIsoDate(dt);
};

const startOfWeekMondayIso = (isoDate) => {
  const dt = new Date(`${isoDate}T00:00:00`);
  const day = dt.getDay(); // 0=Sun, 1=Mon
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  return toIsoDate(dt);
};

const getDateWindow = (startIso) => (
  Array.from({ length: WINDOW_DAYS }, (_, i) => addDaysIso(startIso, i))
);

const dateLabel = (isoDate) => {
  const dt = new Date(`${isoDate}T00:00:00`);
  const day = String(dt.getDate()).padStart(2, '0');
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const year = dt.getFullYear();
  return `${day}-${month}-${year}`;
};

function BanquetCalendar({ token, selectedProperty, selectedOutletId = '', showHelp = false, onOpenFaq }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const today = useMemo(() => toIsoDate(new Date()), []);
  const [windowStart, setWindowStart] = useState(startOfWeekMondayIso(today));
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState([]);
  const [halls, setHalls] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHalls, setLoadingHalls] = useState(false);
  const [cellPopup, setCellPopup] = useState({ open: false, hallName: '', date: '', rows: [] });

  const dates = useMemo(() => getDateWindow(windowStart), [windowStart]);
  const windowEnd = dates[dates.length - 1];

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const load = async () => {
    if (!chainId || !propertyId) return setError('Select property first');
    setLoading(true);
    setError('');
    try {
      const q = new URLSearchParams({ from: windowStart, to: windowEnd });
      if (status) q.set('status', status);
      const [cRes, hRes] = await Promise.all([
        fetch(`/api/v1/banquet/calendar?${q.toString()}`, { headers }),
        fetch(`/api/v1/banquet/calendar/revenue-heatmap?${q.toString()}`, { headers })
      ]);
      const c = await parseJson(cRes);
      const h = await parseJson(hRes);
      if (!cRes.ok) throw new Error(c.error || 'Failed to load calendar');
      if (!hRes.ok) throw new Error(h.error || 'Failed to load heatmap');
      setRows(Array.isArray(c.data) ? c.data : []);
      setHeatmap(Array.isArray(h.data) ? h.data : []);
    } catch (err) {
      setError(err?.message || 'Calendar load failed');
    } finally {
      setLoading(false);
    }
  };

  const loadHalls = async () => {
    if (!chainId || !propertyId) return;
    setLoadingHalls(true);
    try {
      const res = await fetch('/api/v1/banquet/halls', { headers });
      const parsed = await parseJson(res);
      if (!res.ok) throw new Error(parsed.error || 'Failed to load halls');
      const list = Array.isArray(parsed.data) ? parsed.data : [];
      setHalls(list.filter((h) => h.is_active !== false));
    } catch (err) {
      setError(err?.message || 'Hall load failed');
    } finally {
      setLoadingHalls(false);
    }
  };

  useEffect(() => {
    loadHalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, propertyId, outletId, token]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowStart, status, chainId, propertyId, outletId, token]);

  const matrix = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const d = String(r.event_date || '').slice(0, 10);
      const key = `${r.hall_id}__${d}`;
      const existing = map.get(key) || [];
      existing.push(r);
      map.set(key, existing);
    });
    return map;
  }, [rows]);

  const shiftWindow = (offsetDays) => {
    setWindowStart((prev) => addDaysIso(prev, offsetDays));
  };

  const bookingCellFill = (statusValue) => {
    const v = String(statusValue || '').toUpperCase();
    if (v === 'CONFIRMED') return '#2f6b2f';
    if (v === 'TENTATIVE') return '#4f8a3f';
    if (v === 'BLOCKED') return '#3b5f2a';
    return '#5a8f47';
  };

  const openCellPopup = (hallName, date, cellRows) => {
    if (!cellRows?.length) return;
    setCellPopup({ open: true, hallName, date, rows: cellRows });
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary)' }}>Banquet - Calendar</h2>
      <BanquetHelpPanel visible={showHelp} onOpenFaq={onOpenFaq} />
      {error ? <div className="error-banner">{error}</div> : null}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem', alignItems: 'center' }}>
        <button onClick={() => shiftWindow(-WINDOW_DAYS)} disabled={loading}>Previous 7 Days</button>
        <button onClick={() => setWindowStart(startOfWeekMondayIso(today))} disabled={loading}>Current 7 Days</button>
        <button onClick={() => shiftWindow(WINDOW_DAYS)} disabled={loading}>Next 7 Days</button>
        <div style={{ fontWeight: 600, color: '#334155' }}>{windowStart} to {windowEnd}</div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="AVAILABLE">AVAILABLE</option>
          <option value="TENTATIVE">TENTATIVE</option>
          <option value="CONFIRMED">CONFIRMED</option>
          <option value="BLOCKED">BLOCKED</option>
        </select>
        <button onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>

      <div style={{ border: '1px solid #000000', borderRadius: '10px', overflow: 'auto', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1320px', color: '#0f172a' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '0.55rem', background: '#ffffff', border: '1px solid #000000', position: 'sticky', left: 0, zIndex: 3, color: '#0f172a' }}>Halls</th>
              {dates.map((d) => (
                <th key={d} style={{ textAlign: 'center', padding: '0.45rem', background: '#ffffff', border: '1px solid #000000', minWidth: '96px', color: '#0f172a' }}>
                  <div style={{ fontSize: '0.78rem', color: '#0f172a' }}>{dateLabel(d)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {halls.map((hall) => (
              <tr key={hall.hall_id}>
                <td style={{ padding: '0.55rem', border: '1px solid #000000', position: 'sticky', left: 0, background: '#4e6528', zIndex: 2, color: '#ffffff' }}>
                  <div style={{ fontWeight: 600 }}>{hall.hall_name}</div>
                </td>
                {dates.map((d) => {
                  const cellRows = matrix.get(`${hall.hall_id}__${d}`) || [];
                  if (!cellRows.length) {
                    return <td key={`${hall.hall_id}-${d}`} style={{ textAlign: 'center', border: '1px solid #000000', background: '#ffffff', color: '#111827' }}> </td>;
                  }
                  const top = cellRows[0];
                  return (
                    <td
                      key={`${hall.hall_id}-${d}`}
                      style={{ border: '1px solid #000000', padding: '0.25rem 0.3rem', verticalAlign: 'top', background: bookingCellFill(top.status), cursor: 'pointer' }}
                      onClick={() => openCellPopup(hall.hall_name, d, cellRows)}
                      title="Click to view booking details"
                    >
                      <div style={{ display: 'grid', gap: '0.22rem' }}>
                        {cellRows.map((row) => (
                          <div
                            key={row.calendar_id}
                            style={{ background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.45)', borderRadius: '6px', padding: '0.26rem' }}
                            title={`${row.status} | ${row.event_name || row.customer_name || 'Booking'} | ${Number(row.total_value || 0).toFixed(2)}`}
                          >
                            <div style={{ fontSize: '0.69rem', fontWeight: 700, color: 'var(--theme-bg-light)' }}>{row.status}</div>
                            <div style={{ fontSize: '0.68rem', color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row.event_name || row.customer_name || 'Booking'}
                            </div>
                            <div style={{ fontSize: '0.67rem', color: '#ecfeff' }}>{Number(row.total_value || 0).toFixed(0)}</div>
                          </div>
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            {!halls.length ? (
              <tr>
                <td colSpan={WINDOW_DAYS + 1} style={{ padding: '0.8rem', color: 'var(--theme-text-secondary)' }}>
                  {loadingHalls ? 'Loading halls...' : 'No halls found'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {cellPopup.open ? (
        <div
          onClick={() => setCellPopup({ open: false, hallName: '', date: '', rows: [] })}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(860px, 96vw)',
              maxHeight: '86vh',
              overflow: 'auto',
              background: '#ffffff',
              borderRadius: '10px',
              border: '1px solid #0f172a',
              boxShadow: '0 18px 45px rgba(15,23,42,0.35)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8rem 0.9rem', borderBottom: '1px solid var(--theme-border)' }}>
              <div style={{ color: '#0f172a' }}>
                <div style={{ fontWeight: 700 }}>Bookings on {dateLabel(cellPopup.date)}</div>
                <div style={{ fontSize: '0.86rem', color: '#475569' }}>Hall: {cellPopup.hallName} | Count: {cellPopup.rows.length}</div>
              </div>
              <button onClick={() => setCellPopup({ open: false, hallName: '', date: '', rows: [] })}>Close</button>
            </div>
            <div style={{ padding: '0.8rem 0.9rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '0.4rem' }}>Booking ID</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '0.4rem' }}>Status</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '0.4rem' }}>Event</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '0.4rem' }}>Customer</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '0.4rem' }}>Slot</th>
                    <th style={{ textAlign: 'right', borderBottom: '1px solid #cbd5e1', padding: '0.4rem' }}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {cellPopup.rows.map((row) => (
                    <tr key={row.calendar_id}>
                      <td style={{ borderBottom: '1px solid var(--theme-border)', padding: '0.4rem' }}>{row.booking_id || '-'}</td>
                      <td style={{ borderBottom: '1px solid var(--theme-border)', padding: '0.4rem' }}>{row.status || '-'}</td>
                      <td style={{ borderBottom: '1px solid var(--theme-border)', padding: '0.4rem' }}>{row.event_name || '-'}</td>
                      <td style={{ borderBottom: '1px solid var(--theme-border)', padding: '0.4rem' }}>{row.customer_name || '-'}</td>
                      <td style={{ borderBottom: '1px solid var(--theme-border)', padding: '0.4rem' }}>{row.slot_type || '-'}</td>
                      <td style={{ borderBottom: '1px solid var(--theme-border)', padding: '0.4rem', textAlign: 'right' }}>{Number(row.total_value || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '1rem', marginTop: '1rem' }}>
        <div>
          <h4>Daily Revenue</h4>
          <div style={{ maxHeight: '420px', overflow: 'auto', border: '1px solid var(--theme-border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Date</th>
                  <th style={{ textAlign: 'right' }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {heatmap.map((h) => (
                  <tr key={h.event_date}>
                    <td>{String(h.event_date || '').slice(0, 10)}</td>
                    <td style={{ textAlign: 'right' }}>{Number(h.revenue || 0).toFixed(2)}</td>
                  </tr>
                ))}
                {!heatmap.length ? <tr><td colSpan={2}>No revenue data</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4>Window Summary</h4>
          <ul style={{ border: '1px solid var(--theme-border)', padding: '0.75rem 1rem', maxHeight: '420px', overflow: 'auto', margin: 0 }}>
            <li>Days in view: {WINDOW_DAYS}</li>
            <li>Window start: {windowStart}</li>
            <li>Window end: {windowEnd}</li>
            <li>Halls loaded: {halls.length}</li>
            <li>Calendar rows: {rows.length}</li>
            <li>Status filter: {status || 'All'}</li>
            <li>
              Total window revenue:{' '}
              {heatmap.reduce((sum, row) => sum + Number(row.revenue || 0), 0).toFixed(2)}
            </li>
            {rows.slice(0, 8).map((r) => (
              <li key={r.calendar_id} style={{ marginTop: '0.25rem' }}>
                {String(r.event_date || '').slice(0, 10)} | {r.hall_name} | {r.status}
              </li>
            ))}
            {!rows.length ? <li>No calendar rows in this 7-day window</li> : null}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default BanquetCalendar;
